import { GetServerSidePropsContext, PreviewData } from 'next';
import { ParsedUrlQuery } from 'querystring';
export type NextContext = GetServerSidePropsContext<ParsedUrlQuery, PreviewData>;
export const isSafari =
  typeof window !== 'undefined' &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
export const isMac =
  typeof window !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

export const ctrlOrCMD = isMac ? '⌘' : 'ctrl';
export const backspace = isMac ? '⌫' : 'Delete';
export const shift = isMac ? '⇧' : '⇧';

export const isIos =
  typeof window !== 'undefined' &&
  (!!navigator.userAgent.match(/iPad|iPhone|iPod/i) ||
    (isMac && navigator.maxTouchPoints > 0));
/**
 * Public marketing tenant used by tenant-less marketing routes (e.g.
 * `/phonics-practice/...`) so unauth requests to public CDN endpoints can
 * still resolve a signed URL. The backend gates `x-api-tenant` on every
 * public image request even though the file itself is public.
 */
export const MARKETING_LANDING_TENANT_ID = 'a7cdcbe5-cddc-5210-a452-d5d106b4266f';

/** Top-level paths that are tenant-less marketing routes. */
const MARKETING_LANDING_PATHS = new Set<string>(['phonics-practice']);

export const getDestinationTenantFromUrl = (activeTenantId?: string) => {
  if (typeof window !== 'undefined') {
    const destinationTenantId = window.location.pathname.split('/')[1];
    if (!destinationTenantId && !activeTenantId) {
      throw new Error(
        'getDestinationTenantFromUrl Should not be called until we have a destination tenant',
      );
    }
    if (destinationTenantId === 'join') {
      throw new Error('Should not use join as a tenant destination');
    }
    if (destinationTenantId === 'fromtemplate') {
      throw new Error('Should not use join as a tenant destination');
    }
    if (destinationTenantId === 'share') {
      throw new Error('Should not use share as a tenant destination');
    }
    if (destinationTenantId === 'discover' && activeTenantId) {
      return activeTenantId;
    }
    if (destinationTenantId === 'feedback' && activeTenantId) {
      return activeTenantId;
    }
    if (destinationTenantId === 'ai' && activeTenantId) {
      return activeTenantId;
    }
    if (destinationTenantId === 'generators' && activeTenantId) {
      return activeTenantId;
    }
    if (MARKETING_LANDING_PATHS.has(destinationTenantId)) {
      return activeTenantId ?? MARKETING_LANDING_TENANT_ID;
    }
    if (activeTenantId) {
      return activeTenantId;
    }
    return destinationTenantId;
  }
};

export const getOrigin = () => {
  try {
    return window.location.origin;
  } catch (e) {
    // this is now in the service worker

    return self.origin;
  }
};

export const isBrowser = () => {
  if (typeof window !== 'undefined') {
    // Client-side-only code
    return true;
  }
  return false;
};

export const isSafeRedirect = (
  redirect: string | string[] | undefined,
): redirect is string => {
  if (!redirect || typeof redirect !== 'string') return false;
  if (!redirect.startsWith('/')) return false;
  if (redirect.startsWith('//')) return false;
  if (redirect.startsWith('/\\')) return false;
  return true;
};

export const getLoginRedirect = (context: NextContext) => {
  const target = context.resolvedUrl || '/';
  return {
    redirect: {
      permanent: false,
      destination: `/login?redirect=${encodeURIComponent(target)}`,
    },
  };
};
